import CustomerApplicationHelper, {
	checkIfWeNeedANewApplication,
	createCustomerApplicationFromCustomer,
	hasCustomerApplication,
	isCreditApplicationComplete,
	isEditApplicationPastDayLimit,
} from '@core/helpers/customer-application-helper';
import $modal from '@core/services/modal';
import api from '@core/services/api/_api';
import Application from '@core/classes/Applications/Application';
import ApplicationsBusinessInfo from '@core/classes/Applications/ApplicationsBusinessInfo';
import ApplicationsPreFilledData from '@core/classes/Applications/ApplicationsPreFilledData';
import CustomerRecord from '@core/classes/Customer/CustomerRecord';
import { CustomerType } from '@core/classes/SharedEnums';
import FIMenuCustomer from '@core/classes/FIMenuCustomer';
import LogRocket from 'logrocket';
import { MobileVerifyRequest } from '@core/classes/Requests/MobileVerifyRequest';
import modalApplicantInfo from '@core/modals/modalApplicantInfo.vue';
import modalConfirmCustomerInfo from '@core/modals/modalConfirmCustomerInfo.vue';
import modalFullCustomerApplication from '@core/modals/modalFullCustomerApplication.vue';
import ModalInfo from '@core/modals/modalInfo.vue';
import modalRetrieveCustomer from '@core/modals/modalRetrieveCustomer.vue';
import modalSendLinkToCustomer from '@core/modals/modalSendLinkToCustomer.vue';
import { nextTick } from 'vue';
import { Router } from 'vue-router';
import SaturnResponse from '@core/classes/Responses/SaturnResponse';
import { useBusinessStore } from '@core/stores/BusinessStore';
import { useUserStore } from '@core/stores/UserStore';
import util from '@core/services/util';

interface IEmailRequest {
	sendToList: string[];
	isBodyHtml: boolean;
	emailSubject?: string;
	emailBody?: string;
}

interface IPhoneRequest {
	toPhoneNumber: string;
	body: string;
	storeCode: string;
}

export interface IStore {
	additions: any[];
	cdkCode: string;
	cdkSettings: any;
	countyTaxRate: number;
	customerApplicationSettings: any;
	defaultFees: any[];
	emailAddressesForHouseDeals: string;
	emailAddressesForHouseUnwind: string;
	emailAddressesForReports: string;
	gwcCode: string;
	id: string;
	isLeasePaymentTaxed: boolean;
	meta: any;
	nsdCode: string;
	partnerCodes: any[];
	preloads: any[];
	products: any[];
	stateTaxRate: number;
	storeAccountingInfo: any;
	storeAddress: string;
	storeAddressExtra: string;
	storeCity: string;
	storeCode: string;
	storeCounty: string;
	storeDateTime: Date;
	storeFiles: any[];
	storeName: string;
	storePhone: string;
	storeSettings: any;
	storeState: string;
	storeTimeZone: string;
	storeUser: string;
	storeWebsiteUrl: string;
	storeZip: string;
	usWarrantyCompanyCode: string;
	usWarrantyStoreCode: string;
}

/*
    @@@@@@@@@@ --->  READ ME  <--- @@@@@@@@@@

    Let's keep the methods clean and focused. Implement specific solutions directly within the
    main code rather than using helpers. However, if you think an implementation will be reused in different
    places, feel free to add more parameters to the current methods or create new methods.
*/

export async function openRetrieveCustomersModal(
	{ actionOnRetrieveCustomer = (arg: any) => {}, actionOnRetrieveCompany = (arg: any) => {}, canRetrieveCompany = true },
	toggleCreateLead = false,
) {
	const modalParams = {
		name: 'modalRetrieveCustomer',
		passedData: {
			modalView: true,
			actionOnRetrieveCustomer: actionOnRetrieveCustomer,
			actionOnRetrieveCompany: actionOnRetrieveCompany,
			canRetrieveCompany,
			canCreateLeads: toggleCreateLead,
			openModalApplicantInfo: openModalApplicantInfo,
		},
		backdrop: true,
	};
	$modal.open(modalRetrieveCustomer, modalParams);
}

/**
 * Opens a modal to display applicantInfoPanel
 * @param store
 * @param customer
 * @returns
 */
export async function openModalApplicantInfo(store: any, customer = new FIMenuCustomer()) {
	const modalParams = {
		name: 'modalApplicantInfo',
		passedData: {
			title: 'Create Customer Application',
			prefilledData: new ApplicationsPreFilledData(),
			acceptText: 'Create',
			cancelText: 'Close',
			isCustomer: true,
			customer: customer,
			store: store,
		},
		backdrop: false,
	};

	return $modal.open(modalApplicantInfo, modalParams);
}

/**
 * This method creates a customer application from an FIMenu Customer. Ideally should be customer
 *
 * @param storeCode
 * @param customer FIMenuCustomer
 * @param lng Either "English" or "Spanish"
 */
export async function openModalCreateCustomerApplicationFromCustomer(storeCode: string, customer: FIMenuCustomer, lng = 'English') {
	const modalParams = {
		name: 'modalConfirmCustomerInfo',
		passedData: {
			title: 'Create Customer Application',
			info: 'Please confirm the e-mail and cellular phone number are accurate.  We will be sending an e-mail and SMS code to create the quote application.',
			id: customer.id,
			customer: customer,
			isCustomer: true,
			acceptText: 'Create',
			cancelText: 'Cancel',
			language: lng,
			storeCode: storeCode,
		},
		backdrop: false,
	};
	$modal.open(modalConfirmCustomerInfo, modalParams);
}

/**
 *  Display a full customer application
 * @param customer FIMenuCustomer
 */
export async function openModalDisplayFullCustomerApplication(customer: FIMenuCustomer) {
	$modal.open(modalFullCustomerApplication, {
		name: 'modalFullCustomerApplication',
		passedData: {
			customer: customer,
		},
		backdrop: true,
	});
}

export async function openModalSendLinkToCustomer(
	customer: FIMenuCustomer,
	storeCode: string,
	title: string,
	questionKey: string,
	isSingleQuestionEdit = null as boolean,
	resetDateCompleted = false,
	customerType: CustomerType = CustomerType.Customer,
) {
	$modal.open(modalSendLinkToCustomer, {
		name: 'modalSendLinkToCustomer',
		passedData: {
			title: `${title} Section`,
			generateLink: () => generateApplicationEditLink(customer, storeCode, questionKey, isSingleQuestionEdit, resetDateCompleted, true),
			handleSMSIssue: async () => await handleSMSIssue(customer.id, storeCode, customerType),
		},
		backdrop: true,
		postFunction: (sendLinkMethod: string) => sendLinkToCustomer(customer, storeCode, sendLinkMethod, questionKey, isSingleQuestionEdit, resetDateCompleted),
	});
}

export async function generateApplicationEditLink(
	customer: FIMenuCustomer,
	storeCode: string,
	questionKey: string,
	isSingleQuestionEdit = null as boolean,
	resetDateCompleted = false,
	isInStore = false,
) {
	const response = await api.applications.generateApplicationEditLink({
		questionKey,
		isSingleQuestionEdit,
		resetDateCompleted,
		applicationId: customer.customerApplication.id,
		storeCode: storeCode,
		isInStore: isInStore,
	});

	return response.data;
}

export async function sendLinkToCustomer(
	customer: FIMenuCustomer,
	storeCode: string,
	sendLinkMethod: string,
	questionKey: string,
	isSingleQuestionEdit = null as boolean,
	resetDateCompleted = false,
) {
	let link = await generateApplicationEditLink(customer, storeCode, questionKey, isSingleQuestionEdit, resetDateCompleted);

	const newLinkRequest = {
		originalLink: link,
		domain: 'https://' + window.location.host,
		store: storeCode,
		phoneNumber: customer.customerApplication.preFilledData.phoneNumber,
	};

	let newLink = link;
	await api.applications.getRedirectLink(newLinkRequest).then((response: any) => {
		if (response.data) {
			newLink = response.data;
		}
	});

	if (sendLinkMethod == 'email') {
		const request: any = {
			sendToList: [customer.email],
			isBodyHtml: true,
		};

		if (customer.customerApplication.quoteAppData.language == 'Spanish') {
			request.emailSubject = 'Actualización de Aplicación';
			request.emailBody = `<p>Por favor siga el enlace para actualizar su aplicación: </p><a href=${newLink}>Edit Application</a>`;
		} else {
			request.emailSubject = 'Application Update';
			request.emailBody = `<p>Please follow the link to update your application: </p><a href=${newLink}>Edit Application</a>`;
		}

		api.applications
			.sendEditLink({
				applicationId: customer.customerApplication.id,
				emailRequest: request,
			})
			.then((response: any) => {
				if (response.data) {
					util.toastr('success', 'Success', 'Successfully sent email');
				}
			})
			.catch((error: any) => {
				// eslint-disable-next-line no-console
				console.error('Error', error);
				util.toastr('error', 'Error', 'Failed to send text email');
			});
	} else if (sendLinkMethod == 'text') {
		const request = {
			toPhoneNumber: '+1' + customer.cell,
			body:
				customer.customerApplication.quoteAppData.language == 'Spanish'
					? 'Por favor siga el enlace para actualizar su aplicación: ${newLink}'
					: 'Please follow the link to update your application: ' + newLink,
			storeCode: storeCode,
		};

		api.applications
			.sendEditLink({
				applicationId: customer.customerApplication.id,
				messageRequest: request,
			})
			.then((response: any) => {
				if (response.data) {
					util.toastr('success', 'Success', 'Successfully sent message');
				}
			})
			.catch((error: any) => {
				// eslint-disable-next-line no-console
				console.error('Error', error);
				util.toastr('error', 'Error', 'Failed to send message');
			});
	}
}

//#region ----------Direct Customer Stuff -------  //

export async function sendPortalLink(request: object) {
	try {
		let portalLinkResponse = await api.customerPortal.sendPortalLink(request);
		if (portalLinkResponse.status == 200 && portalLinkResponse.data) {
			util.toastr('success', 'Success', 'Successfully sent.');
			return portalLinkResponse.data;
		}
	} catch (error) {
		util.toastr('error', 'Error', 'Failed to send.');
	}
}

export async function generateTinyUrl(link: string, storeCode: string, phoneNumber: string) {
	const newLinkRequest = {
		originalLink: link,
		domain: 'https://' + window.location.host,
		storeCode: storeCode,
		phoneNumber: phoneNumber,
	};

	try {
		const shortRedirectLink = await api.applications.getRedirectLink(newLinkRequest);
		if (shortRedirectLink.status == 200 && shortRedirectLink.data) {
			return shortRedirectLink.data;
		}
	} catch (error) {
		throw new Error('Error generating short link.');
	}
}

export const getCustomerById = async (id: string) => {
	try {
		const customerResponse = await api.customers.getCustomerById(id);
		if (customerResponse?.data) {
			return customerResponse.data;
		} else return null;
	} catch (error) {
		console.error(error);
	}
};

export const getCustomerAndGenerateLink = async (encryptedId: string): Promise<string | null> => {
	try {
		// decryptId
		if (!encryptedId) return null;
		const decryptedId = util.decryptJSONText(encryptedId);
		const customerId = JSON.parse(decryptedId);

		//Get customer and application by id
		const { customer, customerApplication } = await getInformationForCustomerPortal(customerId);

		const link = await generateLinkForQRCode(customer, null, customer.storeCodes[0], false, customerApplication);
		return link;
	} catch (error) {
		util.toastr('error', 'Error', 'Unable to retrieve information. Please contact support.');
		console.error(error);
	}
};

export const generateLinkForQRCode = async (
	customer: any,
	store: IStore,
	storeCode: string = null,
	shouldCheckIfCustomerApp = true,
	customerApplication: Application = null,
) => {
	const currentStoreCode = store?.storeCode ?? storeCode ?? null;
	if (currentStoreCode == null) return null;

	let currentCustomerApp = customer.customerApplication ?? customerApplication ?? null;

	// currentCustomerApp being null can be the case for customers who were created before CustomerApp launched
	if (shouldCheckIfCustomerApp || currentCustomerApp == null) {
		const newCustomerApp = await checkIfWeNeedANewApplication(customer, null, currentStoreCode);
		// Only update `currentCustomerApp` if `newCustomerApp` is not null or undefined (it entered the if stmt)
		if (newCustomerApp != null) {
			currentCustomerApp = newCustomerApp;
		}
	}

	const phone = customer.cell ?? customer.customerApplication.preFilledData.phoneNumber;
	const phoneLastFour = phone.toString().slice(-4);
	const link: string = `/customer-portal/phoneVerification?emailCode=${currentCustomerApp?.emailCode}&storeCode=${currentStoreCode}&lastFour=${phoneLastFour}`;

	const shortLink = await generateTinyUrl(link, currentStoreCode, phone);
	return shortLink;
};

export async function sendLinkToCustomerForPortal(customer: FIMenuCustomer, language: string, store: IStore, sendLinkMethod: string) {
	if (!customer.customerApplication || CustomerApplicationHelper.isCustomerApplicationExpiredByGlobalSettingsCheck(customer.customerApplication.dateCompleted)) {
		try {
			let customerApp = await createCustomerApplicationFromCustomer(null, customer, false, store.storeCode);
			customer.customerApplication = customerApp;
			if (customerApp != null) return util.toastr('success', 'Success', 'Successfully sent.');
		} catch (error) {
			util.toastr('error', 'Error', 'Error sending link to customer. Try again.');
		}
	}

	let spanishMessage: string = 'Por favor siga el enlace para entrar en su portal';
	let spanishButtonText: string = 'Entra al portal';
	let englishMessage: string = 'Please follow the link to enter your portal';
	let englishButtonText: string = 'Enter portal';
	const phone: string = customer.cell ?? customer.customerApplication.preFilledData.phoneNumber;
	const phoneLastFour: string = phone.toString().slice(-4);
	let link: string = `/customer-portal/phoneVerification?emailCode=${customer.customerApplication?.emailCode}&storeCode=${store.storeCode}&lastFour=${phoneLastFour}`;
	// let link = await redirectToCustomerPortal(customer.id);
	let shortLink = await generateTinyUrl(link, store.storeCode, phone);
	if (sendLinkMethod == 'email') {
		const emailRequest: IEmailRequest = {
			sendToList: [customer.email],
			isBodyHtml: true,
			emailSubject: null,
			emailBody: null,
		};

		if (language == 'Spanish') {
			emailRequest.emailSubject = 'Entra al Portal';
			emailRequest.emailBody = `<p>${spanishMessage}: </p><a href=${shortLink}>${spanishButtonText}</a>`;
		} else {
			emailRequest.emailSubject = 'Enter Portal';
			emailRequest.emailBody = `<p>${englishMessage}: </p><a href=${shortLink}>${englishButtonText}</a>`;
		}

		sendPortalLink({
			applicationId: customer.customerApplication.id,
			emailRequest: emailRequest,
		});
	} else if (sendLinkMethod == 'text') {
		const phoneRequest: IPhoneRequest = {
			toPhoneNumber: '+1' + customer.cell,
			body: language == 'Spanish' ? `${spanishMessage}: ${shortLink}` : `${englishMessage}: ${shortLink}`,
			storeCode: store.storeCode,
		};

		sendPortalLink({
			applicationId: customer.customerApplication.id,
			messageRequest: phoneRequest,
		});
	}
}

/** For manually verifying the OTP */
export async function handleSMSIssue(customerId: string, storeCode: string, customerType: CustomerType) {
	nextTick(() => {
		$modal.open(ModalInfo, {
			name: 'modalInfo',
			passedData: {
				title: 'Notifying General Managers',
				info: `This action will notify all GENERAL MANAGERS located at store: ${storeCode}.\n\nThey will provide you with the One Time Password, on the Customer's behalf.`,
				additionalInfo: 'Please ensure that the customer is who they claim they are before giving them the OTP.',
				acceptText: 'I Accept',
				cancelText: 'Cancel',
			},
			backdrop: true,
			postFunction: async () => {
				try {
					const mobileVerifyRequest = new MobileVerifyRequest({
						customerId,
						storeCode,
						customerType,
					});
					const response = (await api.applications.manuallyVerifyOTP(mobileVerifyRequest))?.data;
					if (response && response.messages.length < 1) {
						util.toastr('success', 'One Time Password', 'General Manager(s) Were Notified!');
					} else {
						util.toastr('error', 'One Time Password', response?.messages[0] ?? 'Error notifying General Manager(s)', 5000);
					}
				} catch (error) {
					util.toastr('error', 'One Time Password', error);
					console.error(error);
				}
			},
		});
	});
}

/**
 * @param customerId
 * @returns This is a direct link to the portal
 */
export async function redirectToCustomerPortal(customerId: string) {
	try {
		const response = await api.customerPortal.redirectToPortal(customerId);

		if (response && response.status == 200 && response.data) {
			return response.data;
		}
	} catch (error) {
		throw new Error('Unable to redirect to customer portal link');
	}
}

export async function getInformationForCustomerPortal(customerId: string) {
	try {
		const response = await api.customerPortal.getCustomerInformation(customerId);
		if (response.status == 200) {
			const responseData = response.data;

			if (responseData.payload && responseData.statusCode == 200) {
				const customer = new CustomerRecord(responseData.payload.customer);
				const customerApplication = new Application(responseData.payload.customerApplication);
				const businessCustomer = responseData.payload.businessCustomer;

				return {
					customer,
					customerApplication,
					businessCustomer,
				};
			}
		}
	} catch (error) {
		console.error(error, 'Error retrieving customer information');
	}
	return null;
}

/** @param {CustomerRecord} businessCustomer  @returns a Customer Record for Business Customer. */
export async function createBusinessCustomer(businessCustomer: ApplicationsBusinessInfo, primaryCustomerId: string | string[], storeCodes: string[]) {
	try {
		const createBusinessCustomerRequest = {
			businessCustomer: businessCustomer,
			primaryCustomerId: primaryCustomerId,
			storeCodes: storeCodes,
		};

		const businessResponse = await api.customerPortal.createBusinessCustomerFromPortal(createBusinessCustomerRequest);
		if (businessResponse.status == 200 && businessResponse.data.payload !== null) {
			util.toastr('success', 'Success', 'Successfully added business.');
			const businessStore = useBusinessStore();
			const userStore = useUserStore();

			businessStore.setBusiness(businessResponse.data.payload);
			userStore.setBusinessData(businessResponse.data.payload);
			return businessStore;
		}
		if (businessResponse.status == 400 && businessResponse.data.payload == null) {
			util.toastr('error', 'Error', 'Error creating business. Try again.');
			return;
		}
	} catch (error) {
		LogRocket.log('Error creating business customer', error);
		util.toastr('error', 'Error', 'Error adding. Please reach out to support.');
	}
}

/**
 * Maps ApplicationsBusinessInfo to CustomerRecord
 * @param {ApplicationsBusinessInfo} businessInfo
 * @returns {CustomerRecord}
 */
function mapToCustomerRecord(businessInfo: ApplicationsBusinessInfo): CustomerRecord {
	const customerRecord = new CustomerRecord();
	customerRecord.businessInfo = businessInfo;
	return customerRecord;
}

export async function encryptSensitiveData(value: string) {
	try {
		const response = await api.utilities.encrypt(value);
		if (response) {
			const last4: string = value.slice(-4);
			return {
				hashed: response.data.encryptedValue,
				last4: last4,
			};
		}
	} catch (error) {
		console.error(error);
		util.toastr('error', 'Error', 'Error saving. Please re-enter it and try again.');
	}
}

export const sendCodeToNewEmail = async (req: any) => {
	try {
		const response = await api.customerPortal.changeEmailRequest(req);
		if (response) {
			util.toastr('success', 'Success', 'Successfully sent. Please check your email.');
			return response.data.payload;
		}
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error sending code. Please try again.');
	}
};

export const sendCodeToNewPhone = async (req: any) => {
	try {
		const response = await api.customerPortal.changePhoneRequest(req);
		if (response) {
			util.toastr('success', 'Success', 'Successfully sent. Please check your phone.');
			return response.data.payload;
		}
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error sending code. Please try again.');
	}
};

export const verifyCodeToNewEmail = async (req: any) => {
	try {
		const response = await api.customerPortal.verifyNewEmailRequest(req);
		if (response?.data?.payload) {
			util.toastr('success', 'Success', 'Successfully verified!');
			return response.data.payload;
		} else return util.toastr('error', 'Error', 'Error validating code. Please try again.');
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error validating code. Please try again.');
		return null;
	}
};

export const verifyCodeToNewPhoneNumber = async (req: any) => {
	try {
		const response = await api.customerPortal.verifyNewPhoneRequest(req);
		if (response?.data.payload) {
			util.toastr('success', 'Success', 'Successfully verified!');
			return response.data.payload;
		} else return util.toastr('error', 'Error', 'Error validating code. Please try again.');
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error validating code. Please try again.');
		return null;
	}
};

//#endregion

//#region ---------- Widget stuff ------------ //

/**
 * @param {Application} customerApp
 * @returns Either complete or incomplete, for the Credit App widget pill text
 */
export function getCreditAppPillLabel(customerApp: Application, language: string): string {
	if (!customerApp?.id) return '';
	if (hasCustomerApplication(null, customerApp) && isCreditApplicationComplete(null, customerApp)) {
		return language == 'English' ? 'Completed' : 'Completado';
	}
	return language == 'English' ? 'Incomplete' : 'Incompletado';
}

export function getCreditAppPillStatusColor(customerApp: Application) {
	if (hasCustomerApplication(null, customerApp) && isCreditApplicationComplete(null, customerApp)) {
		return 'success';
	}
	return 'error'; // Assuming 'error' is the color code for incomplete
}

export function disableCreditAppButton(customerApp: Application): boolean {
	if (!customerApp?.id) return true;
	return hasCustomerApplication(null, customerApp) && isCreditApplicationComplete(null, customerApp);
}

//#endregion

export function redirectToGarage(router: Router, id: string) {
	router.push({ name: 'garage', query: { id } });
}

export function rerouteHome(router: Router, id: string) {
	router.push({ name: 'home', query: { id } });
}

export function redirectToProfile(router: Router, id: string) {
	router.push({ name: 'profile', query: { id } });
}

export const redirectToBusinessPage = (router: Router, id: string | string[]) => {
	router.push({ name: 'business', query: { id } });
};

export const redirectToEditBusiness = (router: Router, id: string | string[]) => {
	router.push({ name: 'add-business', query: { id } });
};

export const redirectToAddVehiclePage = (router: Router, id: string | string[]) => {
	router.push({ name: 'add-vehicle', query: { id } });
};

const CustomerHelper = {
	rerouteHome,
	sendPortalLink,
	generateTinyUrl,
	getCustomerById,
	redirectToGarage,
	redirectToProfile,
	encryptSensitiveData,
	getCreditAppPillLabel,
	disableCreditAppButton,
	redirectToEditBusiness,
	redirectToBusinessPage,
	createBusinessCustomer,
	redirectToCustomerPortal,
	sendLinkToCustomerForPortal,
	getCustomerAndGenerateLink,
	getCreditAppPillStatusColor,
	getInformationForCustomerPortal,
};

export default CustomerHelper;
